import { Box } from "@chakra-ui/react"
import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"
import PathwrightUI, { useUIThemeContext } from "./PathwrightUI"
import Screensize, { useScreensizeContext } from "./Screensize"

export const paddingScreensizeSmall = Screensize.select({
  sm: 8,
  md: 12,
  lg: 16
})

export const paddingScreensizeMedium = Screensize.select({
  sm: 10,
  md: 15,
  lg: 20
})

export const paddingScreensizeLarge = Screensize.select({
  sm: 12,
  md: 18,
  lg: 24
})

export const getPadding = (size, screensize) => {
  switch (size) {
    case "small":
      return paddingScreensizeSmall({ screensize })
    case "medium":
      return paddingScreensizeMedium({ screensize })
    case "large":
      return paddingScreensizeLarge({ screensize })
  }
}

const View = React.forwardRef((incomingProps, ref) => {
  const props = {
    el: "div",
    screensizeSelect: {},
    size: "medium",
    ...incomingProps
  }
  const theme = useUIThemeContext()
  const screensize = useScreensizeContext()
  // optionally select props based on screensize
  const selectedProps = {
    ...props,
    ...Screensize.select(props.screensizeSelect)({ screensize })
  }

  const {
    emphasis,
    contrast,
    rounded,
    roundedTop,
    roundedBottom,
    shadow,
    background,
    border,
    borderTop,
    borderBottom,
    borderLeft,
    borderRight,
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    separate,
    screensizeSelect,
    size,
    el,
    ...rest
  } = selectedProps

  return (
    <PathwrightUI.Provider emphasis={emphasis || theme.emphasis}>
      <PathwrightUI.Consumer contrast={contrast}>
        {({ boxShadow, borderColor, borderRadius, backgroundColor }) => {
          const roundedTop = selectedProps.roundedTop || rounded
          const roundedBottom = selectedProps.roundedBottom || rounded
          const roundedLeft = selectedProps.roundedLeft || rounded
          const roundedRight = selectedProps.roundedRight || rounded
          const borderTop = selectedProps.borderTop || border || separate
          const borderBottom = selectedProps.borderBottom || border || separate
          const borderLeft = selectedProps.borderLeft || border
          const borderRight = selectedProps.borderRight || border
          const paddingTop = selectedProps.paddingTop || padding
          const paddingBottom = selectedProps.paddingBottom || padding
          const paddingLeft = selectedProps.paddingLeft || padding
          const paddingRight = selectedProps.paddingRight || padding
          return (
            <Box
              {...rest}
              as={el}
              ref={ref}
              className={classNames("View", rest.className, {
                roundedTop,
                roundedBottom,
                roundedLeft,
                roundedRight,
                borderTop,
                borderBottom,
                borderLeft,
                borderRight,
                paddingTop,
                paddingBottom,
                paddingLeft,
                paddingRight,
                separate
              })}
              boxShadow={shadow ? boxShadow : undefined}
              borderTopLeftRadius={
                borderRadius && (roundedTop || roundedLeft)
                  ? borderRadius
                  : undefined
              }
              borderTopRightRadius={
                borderRadius && (roundedTop || roundedRight)
                  ? borderRadius
                  : undefined
              }
              borderBottomLeftRadius={
                borderRadius && (roundedBottom || roundedLeft)
                  ? borderRadius
                  : undefined
              }
              borderBottomRightRadius={
                borderRadius && (roundedBottom || roundedRight)
                  ? borderRadius
                  : undefined
              }
              backgroundColor={
                background || emphasis || contrast ? backgroundColor : undefined
              }
              borderTop={
                borderTop && borderColor
                  ? `1px solid ${borderColor}`
                  : undefined
              }
              borderBottom={
                borderBottom && borderColor
                  ? `1px solid ${borderColor}`
                  : undefined
              }
              borderLeft={
                borderLeft && borderColor
                  ? `1px solid ${borderColor}`
                  : undefined
              }
              borderRight={
                borderRight && borderColor
                  ? `1px solid ${borderColor}`
                  : undefined
              }
              borderColor={borderColor ? borderColor : undefined}
              paddingTop={
                paddingTop ? `${getPadding(size, screensize)}px` : undefined
              }
              paddingBottom={
                paddingBottom ? `${getPadding(size, screensize)}px` : undefined
              }
              paddingLeft={
                paddingLeft ? `${getPadding(size, screensize)}px` : undefined
              }
              paddingRight={
                paddingRight ? `${getPadding(size, screensize)}px` : undefined
              }
              _empty={{
                borderTop: "none",
                borderLeft: "none",
                borderRight: "none",
                borderBottom: "none"
              }}
              _first={separate ? { borderTop: "none" } : {}}
              _last={separate ? { borderBottom: "none" } : {}}
              css={{
                "&.borderBottom:not(:empty) + &.borderTop:not(:empty)": {
                  borderTop: "none"
                }
              }}
            />
          )
        }}
      </PathwrightUI.Consumer>
    </PathwrightUI.Provider>
  )
})

View.propTypes = {
  el: PropTypes.string,
  emphasis: PropTypes.oneOf(["primary", "secondary", "tertiary"]),
  rounded: PropTypes.bool,
  roundedTop: PropTypes.bool,
  roundedBottom: PropTypes.bool,
  roundedLeft: PropTypes.bool,
  roundedRight: PropTypes.bool,
  shadow: PropTypes.bool,
  background: PropTypes.bool,
  contrast: PropTypes.number,
  border: PropTypes.bool,
  borderTop: PropTypes.bool,
  borderBottom: PropTypes.bool,
  padding: PropTypes.bool,
  paddingTop: PropTypes.bool,
  paddingBottom: PropTypes.bool,
  paddingLeft: PropTypes.bool,
  paddingRight: PropTypes.bool,
  separate: PropTypes.bool,
  screensizeSelect: PropTypes.object,
  size: PropTypes.oneOf(["small", "medium", "large"])
}

View.Primary = React.forwardRef((props, ref) => (
  <View {...props} ref={ref} emphasis="primary" />
))

View.Secondary = React.forwardRef((props, ref) => (
  <View {...props} ref={ref} emphasis="secondary" />
))

View.Tertiary = React.forwardRef((props, ref) => (
  <View {...props} ref={ref} emphasis="tertiary" />
))

export default View
